import { getOwner } from '@ember/owner';
import { once } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import Model from '@ember-data/model';
import type Transform from '@ember-data/serializer/transform';

import { equal } from 'webapp/utils/common';

export type Value = unknown;

type BufferCacheInterface = Record<string, Value>;

export default class Buffer {
	@tracked _buffer: BufferCacheInterface;

	model: Model;

	get bufferedProperties(): string[] {
		return Object.keys(this._buffer);
	}

	get hasBufferedChanges(): boolean {
		return this.bufferedProperties.length > 0;
	}

	constructor(model: Model) {
		this.model = model;
		this._buffer = {};
	}

	// same as this.model[prop], the buffered props are read by name
	readModelProp(prop: string): Value {
		return Reflect.get(this.model, prop);
	}

	getBuffered(prop: string): Value {
		return prop in this._buffer ? this._buffer[prop] : this.readModelProp(prop);
	}

	setBuffered(prop: string, value: Value) {
		const savedValue = this.readModelProp(prop),
			bufferedValue = prop in this._buffer ? this._buffer[prop] : savedValue;

		// if our model is ED Model, we need to also do typecasting
		if (this.model instanceof Model) {
			try {
				const meta = (this.model.constructor as typeof Model).attributes.get(prop)!,
					transform = getOwner(this.model)!.lookup(`transform:${meta.type}`) as Transform;

				// skip serialize for model properties that are null and allowNull is true
				if (!(value === null && meta.options?.allowNull)) {
					if (transform) {
						value = transform.serialize(value, {});
					}
				}
			} catch (_e) {
				// skip
			}
		}

		if (!equal(bufferedValue, value)) {
			if (equal(savedValue, value)) {
				delete this._buffer[prop];
			} else {
				this._buffer[prop] = value;
			}

			once(this, this.triggerBufferUpdate);
		} else if (['goalValuesHistory', 'dsMap'].includes(prop)) {
			// special hack! yay!
			this._buffer[prop] = value;
			once(this, this.triggerBufferUpdate);
		}

		return value;
	}

	applyBufferedChanges(onlyTheseProps?: string[]): void {
		Object.keys(this._buffer).forEach((prop: string) => {
			if (!onlyTheseProps || onlyTheseProps.includes(prop)) {
				Object.assign(this.model, { [prop]: this._buffer[prop] }); // same as this.model[prop] = ...
			}
		});

		this._cleanupBuffer(onlyTheseProps);
	}

	discardBufferedChanges(onlyTheseProps?: string[]): void {
		this._cleanupBuffer(onlyTheseProps);
	}

	triggerBufferUpdate(): void {
		// trigger update for tracked
		this._buffer = this._buffer;
	}

	_resetBuffer(): void {
		this._buffer = {};
	}

	_cleanupBuffer(onlyTheseProps?: string[]): void {
		if (onlyTheseProps) {
			onlyTheseProps.forEach((prop: string) => {
				delete this._buffer[prop];
			});
		} else {
			// so its not run in same runloop
			once(this, this._resetBuffer);
		}

		once(this, this.triggerBufferUpdate);
	}
}
